/**
 * tenantClient — identificação do cliente/tenant da instância no front.
 *
 * Usado pelo overlay de rotas por cliente (App.tsx → loadClientRoutes).
 *
 * Resolução:
 *   1. VITE_CLIENT_CODE (build-time, instâncias dedicadas)
 *   2. Subdomínio do hostname (ex.: copa.trademarketingforce.com → "copa"),
 *      aceito só se existir src/clients/<subdomínio>/routes.tsx.
 *
 * A lista de clientes não é mais escrita à mão (a lista fixa antiga divergia
 * do overlay e não tinha sherwin): ela sai dos arquivos
 * src/clients/<código>/routes.tsx, exatamente o que loadClientRoutes consegue
 * carregar [TMF9-65839]. O clientCode NÃO vem do app-contract aqui: as rotas do
 * overlay são montadas no boot, antes do login, e GET /api/platform/app-contract
 * exige token.
 *
 * @since 2026-07-10
 * @issue TMF9-58844
 */

/** Clientes com overlay de rotas (src/clients/<código>/routes.tsx). */
const OVERLAY_CLIENTS: readonly string[] = Object.keys(
  import.meta.glob('/src/clients/*/routes.tsx', { eager: false })
).map((path) => path.split('/')[3])

/** Códigos de cliente com overlay de rotas. */
export function overlayClients(): readonly string[] {
  return OVERLAY_CLIENTS
}

/**
 * Detecta o código do cliente a partir do subdomínio do hostname.
 * localhost/*.local/*.test não identificam cliente (dev sem overlay).
 */
export function detectClientFromDomain(): string | undefined {
  const hostname = window.location.hostname
  if (!hostname || hostname === 'localhost' || hostname.endsWith('.local') || hostname.endsWith('.test')) {
    return undefined
  }
  const subdomain = hostname.split('.')[0]
  return OVERLAY_CLIENTS.includes(subdomain) ? subdomain : undefined
}

/** Código do cliente da instância (VITE_CLIENT_CODE > subdomínio), se houver. */
export function getTenantClientCode(): string | undefined {
  return import.meta.env.VITE_CLIENT_CODE || detectClientFromDomain()
}
